/**
 * DryRunPanel — the gate in front of a real dispatch (CONTRACT §8.4).
 *
 *   "Dry-run must show resolved source key, recipients, and the exact column list
 *    before send."
 *
 * It opens on what the run resolves to — window, coverage, shape, row counts and the
 * link expiry — because those are the §8.4 deliverables. The negative result about
 * coverage is stated once, in the row whose label asks for it, rather than as a box of
 * prose above everything the contract requires this screen to show.
 *
 * So this panel answers the four questions an operator actually has:
 *
 *   1. WHICH FILE will it read?   The resolved key, whether S3 has it, when it was
 *                                 written, and HOW MUCH OF THE DAY IS IN IT. A file older
 *                                 than the window means `stale` and nothing may be sent
 *                                 (§8.3). A file that is fresh but stops early is a
 *                                 part day (CONTRACT-COVERAGE): it is refused for a
 *                                 client delivery too, unless that client has opted in,
 *                                 and the two refusals are shown as the different things
 *                                 they are. The file's last-modified time is NOT its
 *                                 coverage — it runs later than the data inside it.
 *   2. WHO will receive it?       The resolved to/cc/bcc list, with suppressed
 *                                 recipients called out rather than quietly dropped.
 *   3. WHAT COLUMNS ship?         The exact emitted list in order, each tagged with its
 *                                 policy tier, plus every column that was asked for and
 *                                 withheld, with the reason (§6).
 *   4. HOW BIG is it?             Rows in, rows out, estimated bytes.
 *
 * The footer carries a one-line verdict beside the button — sources, recipients,
 * columns and rows — so the evidence and the irreversible action are on screen together.
 *
 * Only then does "Send now" become available, and only when the run is not stale, not
 * refused by the §3 guardrail, and has someone to send to. Every one of those states
 * disables the button *and* says why on the button itself — a greyed-out control with
 * no explanation is how an operator ends up re-running a dry run five times.
 *
 * Recipient addresses are shown in full here on purpose. CONTRACT §8.6 masks phone
 * numbers, borrower names and tokens; the whole value of this screen is confirming
 * exactly which mailbox is about to receive a client's borrower data.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import Badge, { CoverageBadge, PolicyBadge } from '@/components/Badge';
import Button from '@/components/Button';
import ConfirmDialog from '@/components/ConfirmDialog';
import Modal from '@/components/Modal';
import Table, { type Column } from '@/components/Table';
import { useToast } from '@/components/Toast';
import { api, errorMessage } from '@/lib/api';
import {
  basename,
  coverageOf,
  describeWindow,
  formatBytes,
  formatCount,
  humaniseColumn,
  istDateRange,
  istDateTime,
  istDateTimeExact,
  pluralise,
  timeAgo,
} from '@/lib/format';
import { MAX_LINK_TTL_DAYS, NOT_CONTACTED, NOT_RANKED, reportShapeInfo } from '@/lib/types';
import type { Client, DryRunResult, RunRecipient, Schedule } from '@/lib/types';

/**
 * Per-object source detail. `source_keys` / `missing_source_keys` are the contract
 * shape; a backend that also reports LastModified (it must know it — that is how §8.3
 * staleness is decided) can send it as `source_objects` or `source_last_modified` and
 * this panel will show it. Absent, the row says so rather than inventing a time.
 */
export interface DryRunSourceObject {
  key: string;
  exists: boolean;
  last_modified?: string | null;
  bytes?: number | null;
}

function readSourceObjects(result: DryRunResult): DryRunSourceObject[] {
  const missing = new Set(result.missing_source_keys ?? []);
  const extra = result as unknown as {
    source_objects?: unknown;
    source_last_modified?: unknown;
  };

  const meta = new Map<string, { last_modified?: string | null; bytes?: number | null }>();

  if (Array.isArray(extra.source_objects)) {
    for (const entry of extra.source_objects) {
      if (!entry || typeof entry !== 'object') continue;
      const row = entry as Record<string, unknown>;
      const key =
        typeof row.key === 'string' ? row.key : typeof row.s3_key === 'string' ? row.s3_key : null;
      if (!key) continue;
      meta.set(key, {
        last_modified: typeof row.last_modified === 'string' ? row.last_modified : null,
        bytes:
          typeof row.bytes === 'number'
            ? row.bytes
            : typeof row.size === 'number'
              ? row.size
              : null,
      });
      if (row.exists === false) missing.add(key);
    }
  }

  if (extra.source_last_modified && typeof extra.source_last_modified === 'object') {
    for (const [key, value] of Object.entries(extra.source_last_modified as Record<string, unknown>)) {
      if (typeof value !== 'string') continue;
      meta.set(key, { ...(meta.get(key) ?? {}), last_modified: value });
    }
  }

  const keys = Array.from(
    new Set([...(result.source_keys ?? []), ...Array.from(missing), ...Array.from(meta.keys())]),
  );

  return keys.map((key) => ({ key, exists: !missing.has(key), ...(meta.get(key) ?? {}) }));
}

/** CONTRACT §6 tier for one emitted column, from the client's policy. */
export function columnTier(
  client: Client | null | undefined,
  column: string,
): 'default' | 'opt_in' | 'never' {
  const policy = client?.column_policy;
  if (!policy) return 'default';
  if (policy.never?.includes(column)) return 'never';
  if (policy.opt_in?.includes(column)) return 'opt_in';
  return 'default';
}

/**
 * to / cc / bcc is a CLASSIFICATION — a fact about the row that cannot be wrong and
 * never changes on its own — so it is a mark, not a pill, and never the accent. Teal
 * marks what you can act on, and there is nothing to click on a recipient row.
 */
function kindBadge(kind: RunRecipient['kind']) {
  if (kind === 'bcc') {
    return (
      <Badge
        variant="plain"
        glyph="·"
        title="Blind copy — other recipients do not see this address"
      >
        bcc
      </Badge>
    );
  }
  if (kind === 'cc') {
    return (
      <Badge variant="plain" glyph="·" title="Copied">
        cc
      </Badge>
    );
  }
  return (
    <Badge variant="plain" glyph="→" title="Primary recipient">
      to
    </Badge>
  );
}

export interface DryRunPanelProps {
  open: boolean;
  schedule: Schedule | null;
  /** Supplies the column policy tiers and the link TTL. Optional; the panel degrades. */
  client?: Client | null;
  onClose: () => void;
  /** Fired after a real dispatch is queued from inside the panel. */
  onDispatched?: (runId: string) => void;
  /** Every dry-run outcome, so a parent can record that the gate was passed. */
  onResult?: (result: DryRunResult | null) => void;
}

export function DryRunPanel({
  open,
  schedule,
  client,
  onClose,
  onDispatched,
  onResult,
}: DryRunPanelProps) {
  const toast = useToast();
  const [result, setResult] = useState<DryRunResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ranAt, setRanAt] = useState<Date | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);

  // Every dry run gets a sequence number; a response from an earlier one never
  // overwrites a later one when an operator hammers "Re-run".
  const sequence = useRef(0);

  // Held in a ref, not a dependency: a parent that passes an inline arrow would
  // otherwise give `run` a new identity every render and the effect below would
  // re-fire a dry run forever.
  const onResultRef = useRef(onResult);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  const scheduleId = schedule?.schedule_id ?? null;

  const run = useCallback(async () => {
    if (!scheduleId) return;
    const ticket = (sequence.current += 1);
    setLoading(true);
    setError(null);
    try {
      const outcome = await api.schedules.dryRun(scheduleId);
      if (ticket !== sequence.current) return;
      setResult(outcome);
      setRanAt(new Date());
      onResultRef.current?.(outcome);
    } catch (err) {
      if (ticket !== sequence.current) return;
      setResult(null);
      setRanAt(null);
      setError(errorMessage(err));
      onResultRef.current?.(null);
    } finally {
      if (ticket === sequence.current) setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => {
    if (!open || !scheduleId) return;
    setResult(null);
    setRanAt(null);
    setError(null);
    setConfirmSend(false);
    void run();
    // `run` is stable per scheduleId; re-running on every render would loop.
  }, [open, scheduleId, run]);

  const sources = result ? readSourceObjects(result) : [];
  const recipients = result?.recipients ?? [];
  const deliverable = recipients.filter((entry) => entry.status !== 'suppressed');
  const columns = result?.columns ?? [];
  const suppressed = result?.suppressed_columns ?? [];
  const unranked = result?.unranked_dispositions ?? [];
  // CONTRACT-FLOWS: which flows this client declares, which of them have no best outcome,
  // and how many sampled rows landed in one. All empty for a client with no flows.
  const flows = result?.flows ?? [];
  const flowsWithoutRanking = flows.filter((entry) => !entry.ranked);
  const flowCounts = Object.entries(result?.flow_counts ?? {});
  const rowsNotRanked = result?.rows_not_ranked_sample ?? 0;
  const unmatchedFlows = result?.unmatched_flows ?? [];
  const notRankedLabel = result?.not_ranked_label ?? NOT_RANKED;
  const ttlDays = Math.min(client?.link_ttl_days ?? MAX_LINK_TTL_DAYS, MAX_LINK_TTL_DAYS);
  const expiryPreview = new Date(Date.now() + ttlDays * 86_400_000);

  /**
   * CONTRACT-COVERAGE §7 — and this is the important negative result on this screen.
   *
   * A dry run CANNOT tell you whether the source is a part day. It reads a bounded prefix,
   * and these exports are ordered by attempt round, so the latest dialed time inside a
   * prefix is not the latest in the file. The backend therefore returns `unmeasured` here
   * every single time, deliberately, and carries the reason in `coverage.note`.
   *
   * So there is no part-day send blocker here, and there must not be one: a blocker that
   * could never fire reads as proof the file is whole. What this panel can state truthfully
   * is what the RUN will do if it finds a part day — which is the question an operator
   * actually has before pressing Send now.
   *
   * Note this reads `result.coverage`, the nested block, not the flat run fields. A dry-run
   * result does not carry those and never will.
   */
  const coverage = coverageOf(result?.coverage);
  const allowsPartial = client?.allow_partial_window === true;
  // Sample-only, and labelled as such everywhere it is shown. The run's own figure is a
  // different and much larger quantity.
  const misalignedInSample = result?.rows_misaligned_in_sample ?? 0;

  const sendBlocker: string | null = !result
    ? 'Run a dry run before sending.'
    : result.guardrail_error
      ? 'The window guardrail refuses this run.'
      : result.stale
        ? 'Source is stale — nothing may be sent.'
        : (result.missing_source_keys?.length ?? 0) > 0
          ? 'A source object is missing.'
          : deliverable.length === 0
            ? 'No deliverable recipients resolved.'
            : null;

  const send = async (): Promise<void> => {
    if (!schedule || sendBlocker) return;
    setSending(true);
    try {
      const queued = await api.schedules.runNow(schedule.schedule_id);
      toast.success('Dispatch queued', {
        description: `Run ${queued.run_id} is ${queued.status}.`,
        action: onDispatched
          ? { label: 'Open run', onClick: () => onDispatched(queued.run_id) }
          : undefined,
      });
      setConfirmSend(false);
      onDispatched?.(queued.run_id);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
      throw err;
    } finally {
      setSending(false);
    }
  };

  const sourceColumns: Column<DryRunSourceObject>[] = [
    {
      key: 'exists',
      header: 'In S3',
      width: '92px',
      nowrap: true,
      render: (row) =>
        row.exists ? (
          <Badge tone="ok" glyph="✓" title="Object is present">
            present
          </Badge>
        ) : (
          <Badge tone="danger" glyph="✕" terminalBad title="No such object — nothing can be sent">
            missing
          </Badge>
        ),
    },
    {
      // The header promises the key and §8.4 requires it, so the whole key is here as
      // selectable text — the prefix is the part that distinguishes one client's path
      // from another's, and it used to be reachable only by hovering.
      key: 'key',
      header: 'Source key (read-only bucket)',
      mono: true,
      wrap: true,
      render: (row) => {
        const file = basename(row.key);
        const prefix = row.key.endsWith(file) ? row.key.slice(0, row.key.length - file.length) : '';
        return (
          <span title={`s3://neuron-report/${row.key}`}>
            <span className="dim">{prefix}</span>
            <span className="strong">{file}</span>
          </span>
        );
      },
    },
    {
      key: 'written',
      header: 'Written (IST)',
      width: '170px',
      nowrap: true,
      render: (row) =>
        row.last_modified ? (
          <span title={istDateTimeExact(row.last_modified)}>
            {istDateTime(row.last_modified)}{' '}
            <span className="dim">· {timeAgo(row.last_modified)}</span>
          </span>
        ) : (
          <span className="dim" title="The dry-run response did not report LastModified for this object">
            not reported
          </span>
        ),
    },
    {
      key: 'bytes',
      header: 'Size',
      align: 'right',
      mono: true,
      width: '96px',
      render: (row) => (row.bytes === null || row.bytes === undefined ? '—' : formatBytes(row.bytes)),
    },
  ];

  const recipientColumns: Column<RunRecipient>[] = [
    {
      key: 'status',
      header: 'State',
      width: '132px',
      nowrap: true,
      render: (row) =>
        row.status === 'suppressed' ? (
          <Badge tone="danger" glyph="⊘" terminalBad title="Suppressed by SES — this address gets nothing">
            suppressed
          </Badge>
        ) : row.status === 'bounced' || row.status === 'complained' ? (
          <Badge tone="warn" glyph="▲" title="Previously bounced or complained">
            {row.status}
          </Badge>
        ) : (
          <Badge tone="ok" glyph="●" title="Deliverable">
            deliverable
          </Badge>
        ),
    },
    // Never truncated, never masked: the whole value of this screen is confirming
    // exactly which mailbox is about to receive a client's borrower data.
    {
      key: 'email',
      header: 'Recipient',
      wrap: true,
      render: (row) => <span className="mono">{row.email}</span>,
    },
    { key: 'kind', header: 'Kind', width: '84px', nowrap: true, render: (row) => kindBadge(row.kind) },
  ];

  const columnRows = columns.map((column, index) => ({ column, index }));
  const emittedColumns: Column<{ column: string; index: number }>[] = [
    // No ordinal column: the header already says "in this order" and the rows are in it.
    {
      key: 'column',
      header: 'Column header as it ships',
      mono: true,
      wrap: true,
      render: (row) => row.column,
    },
    {
      key: 'tier',
      header: 'Policy',
      width: '104px',
      nowrap: true,
      headerTitle: 'CONTRACT §6 — default ships unless turned off, opt-in only when listed',
      render: (row) => <PolicyBadge tier={columnTier(client, row.column)} />,
    },
    {
      key: 'meaning',
      header: 'Reads as',
      width: '300px',
      render: (row) => <span className="dim truncate">{humaniseColumn(row.column)}</span>,
    },
  ];

  const suppressedColumns: Column<{ column: string; reason: string }>[] = [
    { key: 'column', header: 'Withheld column', width: '280px', mono: true, render: (row) => row.column },
    { key: 'reason', header: 'Why', wrap: true, render: (row) => row.reason },
  ];

  // One row per flow: the ones this client configures, plus any the sample carried that it
  // does not. A flow the file has but nobody configured is scored against the client
  // ranking, which may not be its vocabulary at all — so it is shown, not hidden.
  interface FlowRow {
    flow: string;
    ranked: boolean;
    configured: boolean;
    reason: string;
    ranks: number;
    rows: number | null;
  }
  const flowRows: FlowRow[] = [
    ...flows.map((entry) => ({
      flow: entry.flow,
      ranked: entry.ranked,
      configured: true,
      reason: entry.reason ?? '',
      ranks: (entry.effective_ranks ?? entry.disposition_ranks ?? []).length,
      rows: result?.flow_counts?.[entry.flow] ?? null,
    })),
    ...flowCounts
      .filter(([name]) => !flows.some((entry) => entry.flow === name))
      .map(([name, count]) => ({
        flow: name,
        ranked: true,
        configured: false,
        reason: '',
        ranks: 0,
        rows: count,
      })),
  ];
  const flowColumns: Column<FlowRow>[] = [
    {
      key: 'ranking',
      header: 'Ranking',
      width: '210px',
      nowrap: true,
      render: (row) =>
        !row.configured ? (
          <Badge tone="warn" glyph="?" pending title="Not configured — uses the client ranking">
            client ranking
          </Badge>
        ) : row.ranked ? (
          <Badge tone="ok" glyph="★" title="Ranked against this flow's own ladder">
            {pluralise(row.ranks, 'outcome')}
          </Badge>
        ) : (
          <Badge tone="neutral" glyph="—" title="No best outcome exists in this flow">
            {notRankedLabel}
          </Badge>
        ),
    },
    {
      key: 'flow',
      header: 'Flow (as the file spells it)',
      width: '240px',
      mono: true,
      render: (row) => row.flow || <span className="dim">(blank)</span>,
    },
    {
      key: 'rows',
      header: 'Rows in sample',
      width: '130px',
      align: 'right',
      mono: true,
      render: (row) => (row.rows === null ? <span className="dim">—</span> : formatCount(row.rows)),
    },
    {
      key: 'why',
      header: 'Why',
      wrap: true,
      render: (row) =>
        row.reason ? <span className="small">{row.reason}</span> : <span className="dim">—</span>,
    },
  ];

  /**
   * The one-line verdict that travels with the Send button in the sticky footer, so the
   * evidence and the irreversible action are on screen together. Every figure in it is
   * also stated in full in its own section above.
   */
  const optInEmitted = columns.filter((column) => columnTier(client, column) === 'opt_in');
  const missingSources = sources.filter((row) => !row.exists).length;
  const verdict = result
    ? [
        sources.length === 0
          ? 'no source key resolved'
          : missingSources > 0
            ? `${missingSources} of ${pluralise(sources.length, 'source')} missing`
            : `${pluralise(sources.length, 'source')} present`,
        pluralise(deliverable.length, 'recipient'),
        `${pluralise(columns.length, 'column')}, ${optInEmitted.length} opt-in`,
        `${formatCount(result.rows_in)} → ${formatCount(result.rows_out)} rows`,
      ].join(' · ')
    : null;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        dismissible={!sending}
        size="xl"
        title="Dry run"
        description={
          schedule
            ? `${schedule.name ?? schedule.schedule_id} · ${schedule.client_id} · nothing is sent until you press Send now.`
            : 'Nothing is sent until you press Send now.'
        }
        footer={
          <>
            {verdict ? (
              <span className="small dim grow" style={{ minWidth: 0 }} title={verdict}>
                {verdict}
              </span>
            ) : (
              <span className="grow" />
            )}
            <Button variant="ghost" onClick={onClose} disabled={sending}>
              Close
            </Button>
            <Button onClick={() => void run()} loading={loading} disabled={sending}>
              Re-run dry run
            </Button>
            <Button
              variant="primary"
              disabled={Boolean(sendBlocker) || loading}
              title={sendBlocker ?? 'Sends the real report and mints the links'}
              onClick={() => setConfirmSend(true)}
            >
              {sendBlocker ?? `Send now to ${pluralise(deliverable.length, 'recipient')}`}
            </Button>
          </>
        }
      >
        <div className="stack">
          {error ? (
            <div className="callout tone-danger" role="alert">
              <span className="callout-glyph" aria-hidden="true">
                ✕
              </span>
              <div className="stack-tight">
                <div className="strong">The dry run failed.</div>
                <div>{error}</div>
              </div>
            </div>
          ) : null}

          {loading && !result ? (
            <div className="cluster">
              <span className="spinner spinner-lg" aria-hidden="true" />
              <span className="muted">
                Resolving the window, locating the source object and counting rows…
              </span>
            </div>
          ) : null}

          {result ? (
            <>
              {result.guardrail_error ? (
                <div className="callout tone-danger" role="alert">
                  <span className="callout-glyph" aria-hidden="true">
                    ✕
                  </span>
                  <div className="stack-tight">
                    <div className="strong">This run would be refused.</div>
                    <div>{result.guardrail_error}</div>
                  </div>
                </div>
              ) : null}

              {result.stale ? (
                <div className="callout tone-warn" role="alert">
                  <span className="callout-glyph" aria-hidden="true">
                    ◈
                  </span>
                  <div className="stack-tight">
                    <div className="strong">Stale — nothing may be sent.</div>
                    <div>
                      {result.stale_reason ??
                        'The source object is missing, or older than the window it is supposed to cover.'}{' '}
                      CONTRACT §8.3: the run is recorded as stale and the client receives nothing,
                      rather than yesterday’s file dressed as today’s.
                    </div>
                  </div>
                </div>
              ) : null}


              <section className="stack-tight">
                <div className="eyebrow">What it resolves to</div>
                <dl className="kv">
                  <dt>Window</dt>
                  <dd>
                    {describeWindow(result.window)} ·{' '}
                    <span className="strong">
                      {istDateRange(result.resolved_from, result.resolved_to)}
                    </span>
                  </dd>

                  <dt>Coverage (IST)</dt>
                  <dd>
                    {/* The coverage answer, stated once, in the row whose label asks for
                        it. It used to be a 205px box at the very top of the panel — the
                        first screenful of the gate in front of an irreversible dispatch
                        was prose about what the console cannot tell you, said twice,
                        above the three things CONTRACT §8.4 requires it to show.

                        The measured branch is unreachable today — the backend answers
                        `unmeasured` for every dry run on purpose — and it is written anyway
                        so that the day a dry run can measure, this screen reports it instead
                        of insisting it cannot. Hard-coding the negative is how a screen ends
                        up lying in the other direction. */}
                    {coverage.measured ? (
                      <div className="stack-tight" style={{ alignItems: 'flex-start' }}>
                        <CoverageBadge coverage={coverage} />
                        <span className="strong">{coverage.summary}</span>
                      </div>
                    ) : (
                      <div className="stack-tight">
                        <span className="strong">Not measured by a dry run</span>
                        {/* The one genuinely actionable sentence: what the RUN will do if
                            it finds a part day. That is the question an operator has
                            before pressing Send now, so it stays in view. */}
                        <div className="small">
                          {allowsPartial ? (
                            <>
                              If the run finds a part day,{' '}
                              <span className="strong">{client?.name ?? 'this client'}</span> has{' '}
                              <code className="mono">allow_partial_window</code> set, so it{' '}
                              <span className="strong">will still send</span> — with the coverage
                              stated in the subject, the body and the report itself.
                            </>
                          ) : (
                            <>
                              If the run finds a part day it will{' '}
                              <span className="strong">send nothing</span>:{' '}
                              {client?.name ?? 'this client'} has no{' '}
                              <code className="mono">allow_partial_window</code>, so a part day is
                              refused the same way a stale source is. Pull it from the Downloads
                              screen instead — an internal download accepts one, because nothing
                              leaves the building.
                            </>
                          )}
                        </div>
                        <details>
                          <summary className="small dim" style={{ cursor: 'pointer' }}>
                            Why a dry run cannot measure it
                          </summary>
                          <div className="stack-tight small" style={{ marginTop: 'var(--s-1)' }}>
                            <div>
                              How far into the window this file reaches is not measured here.
                            </div>
                            <div>
                              {result.coverage?.note ??
                                'A dry-run reads a bounded prefix of the source, and these exports are ordered by attempt round — the latest dialed time inside a prefix is not the latest in the file. How far into the window this export reaches is measured by the run itself, from every row it walks.'}
                            </div>
                            <div className="dim">
                              Measured by the run, from every row it walks. Never estimated from a
                              prefix — an estimate here would be believed exactly because
                              everything else on this screen is real.
                            </div>
                            {result.coverage?.source_last_modified_ist ? (
                              <div>
                                The source was written{' '}
                                <span className="strong">
                                  {result.coverage.source_last_modified_ist}
                                </span>{' '}
                                — an upper bound on its coverage and never the coverage itself.
                                The 16 Sep export was written 15:57 and its last call is 13:29.
                              </div>
                            ) : null}
                          </div>
                        </details>
                      </div>
                    )}
                  </dd>

                  {misalignedInSample > 0 ? (
                    <>
                      <dt>Misaligned (sample)</dt>
                      <dd>
                        {formatCount(misalignedInSample)} row
                        {misalignedInSample === 1 ? '' : 's'}{' '}
                        <span className="strong">in the sample</span> could not be lined up
                        against the header — their values sit under the wrong column names. This
                        is a sample figure, not the file&apos;s; the run counts the whole file.
                      </dd>
                    </>
                  ) : null}

                  <dt>Shape</dt>
                  <dd>
                    {reportShapeInfo(result.report_shape).label} —{' '}
                    {reportShapeInfo(result.report_shape).gloss}
                  </dd>

                  <dt>Rows</dt>
                  <dd className="mono">
                    {formatCount(result.rows_in)} read → {formatCount(result.rows_out)}{' '}
                    {reportShapeInfo(result.report_shape).rowNoun}
                    {result.estimated_bytes ? (
                      <span className="dim"> · about {formatBytes(result.estimated_bytes)}</span>
                    ) : null}
                  </dd>

                  <dt>Links expire</dt>
                  <dd>
                    <span className="num">{ttlDays}</span> days after dispatch — send now and they
                    die <span className="num strong">{istDateTimeExact(expiryPreview)}</span>.
                    <div className="small dim">The file itself is not deleted.</div>
                  </dd>
                </dl>
              </section>

              <section className="stack-tight">
                <div className="eyebrow">Source objects</div>
                <Table
                  columns={sourceColumns}
                  rows={sources}
                  rowKey={(row) => row.key}
                  dense
                  rowTone={(row) => (row.exists ? 'default' : 'danger')}
                  empty={
                    <div className="empty tone-danger">
                      <div className="empty-title">No source key resolved</div>
                      <div className="empty-description">
                        The window produced no object name at all — check the client’s s3_prefix.
                      </div>
                    </div>
                  }
                />
              </section>

              <section className="stack-tight">
                <div className="cluster">
                  <span className="eyebrow">Recipients</span>
                  <span className="small dim">
                    {pluralise(deliverable.length, 'deliverable address', 'deliverable addresses')}
                    {recipients.length !== deliverable.length
                      ? ` · ${recipients.length - deliverable.length} suppressed`
                      : ''}
                  </span>
                </div>
                <Table
                  columns={recipientColumns}
                  rows={recipients}
                  rowKey={(row) => row.recipient_id || row.email}
                  dense
                  rowTone={(row) => (row.status === 'suppressed' ? 'muted' : 'default')}
                  empty={
                    <div className="empty tone-danger">
                      <div className="empty-title">Nobody would receive this</div>
                      <div className="empty-description">
                        The client has no active recipients. Add one before dispatching.
                      </div>
                    </div>
                  }
                />
              </section>

              <section className="stack-tight">
                <div className="cluster">
                  <span className="eyebrow">Exact columns emitted</span>
                  <span className="small dim">
                    {pluralise(columns.length, 'column')}
                    {reportShapeInfo(result.report_shape).columnsNote}
                  </span>
                </div>
                <Table
                  columns={emittedColumns}
                  rows={columnRows}
                  rowKey={(row) => `${row.index}-${row.column}`}
                  dense
                  stickyHeader
                  rowTone={(row) => (columnTier(client, row.column) === 'opt_in' ? 'warn' : 'default')}
                  empty={
                    <div className="empty tone-filtered">
                      <div className="empty-title">No columns resolved</div>
                      <div className="empty-description">
                        Nothing would ship. Check the client’s field_map and column policy.
                      </div>
                    </div>
                  }
                />
              </section>

              {suppressed.length > 0 ? (
                <section className="stack-tight">
                  <div className="cluster">
                    <span className="eyebrow">Asked for, withheld</span>
                    <PolicyBadge tier="never" />
                  </div>
                  <Table
                    columns={suppressedColumns}
                    rows={suppressed}
                    rowKey={(row) => row.column}
                    dense
                  />
                </section>
              ) : null}

              {flows.length > 0 || flowCounts.length > 1 ? (
                <section className="stack-tight">
                  <div className="cluster">
                    <span className="eyebrow">Flows in this window</span>
                    <span className="small dim">
                      {flows.length > 0
                        ? `${pluralise(flows.length, 'flow')} configured · a rank index only means something inside one flow`
                        : 'none configured — one ranking is applied to every campaign below'}
                    </span>
                  </div>
                  <Table
                    columns={flowColumns}
                    rows={flowRows}
                    rowKey={(row) => row.flow}
                    dense
                    rowTone={(row) => (row.ranked ? 'default' : 'muted')}
                  />
                  {rowsNotRanked > 0 ? (
                    <div className="small">
                      {pluralise(rowsNotRanked, 'sampled row')} sit in{' '}
                      {flowsWithoutRanking.map((entry) => entry.flow).join(', ') ||
                        'a flow with no ranking'}
                      , so those borrowers read{' '}
                      <span className="mono">{notRankedLabel}</span> — not{' '}
                      <span className="mono">{NOT_CONTACTED}</span>, which would be a false claim
                      about a call that connected.
                    </div>
                  ) : null}
                  {unmatchedFlows.length > 0 ? (
                    <div className="small dim">
                      Not configured, so scored against the client ranking:{' '}
                      {unmatchedFlows.map((value) => (
                        <code key={value || '(blank)'} className="mono xs">
                          {value || '(blank)'}
                        </code>
                      ))}
                      {result.client_id ? (
                        <>
                          {' · '}
                          <Link className="small" to={`/dispositions/${result.client_id}`}>
                            Configure →
                          </Link>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              ) : null}

              {unranked.length > 0 ? (
                <div className="callout tone-warn">
                  <span className="callout-glyph" aria-hidden="true">
                    ?
                  </span>
                  <div className="stack-tight" style={{ minWidth: 0 }}>
                    <div className="strong">
                      {pluralise(unranked.length, 'disposition')} in this data are not ranked.
                    </div>
                    <div>
                      They are not silently sorted last (CONTRACT §5) — a borrower whose only
                      outcome is one of these gets “Not contacted” until someone ranks it.
                    </div>
                    <div className="cluster" style={{ gap: 'var(--s-1)' }}>
                      {unranked.map((value) => (
                        <code key={value} className="mono xs">
                          {value || '(blank)'}
                        </code>
                      ))}
                    </div>
                    {result.client_id ? (
                      <Link className="small" to={`/dispositions/${result.client_id}`}>
                        Rank them →
                      </Link>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {ranAt ? (
                <div className="small dim">
                  Dry run completed {istDateTimeExact(ranAt)} · {timeAgo(ranAt)}. Re-run it if the
                  source file has been rewritten since.
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmSend}
        tone="warn"
        title="Send this report now?"
        confirmLabel={`Send to ${pluralise(deliverable.length, 'recipient')}`}
        consequence={`Email leaves immediately and cannot be recalled. Each recipient gets a link that works for ${ttlDays} days, until ${istDateTimeExact(expiryPreview)}.`}
        onCancel={() => setConfirmSend(false)}
        onConfirm={send}
      >
        <div className="stack-tight">
          <div>
            {pluralise(result?.rows_out ?? 0, 'row')} to{' '}
            {deliverable.map((entry) => entry.email).join(', ')}
          </div>
          {/* Never truncated. This is the §6 list of call-content columns about to leave
              the building, and it used to end in an ellipsis with no count and no title. */}
          <div className="small" style={{ overflowWrap: 'anywhere' }}>
            {optInEmitted.length === 0 ? (
              <span className="dim">No opt-in call content is included.</span>
            ) : (
              <>
                <span className="strong">
                  {pluralise(optInEmitted.length, 'opt-in column')}:
                </span>{' '}
                <span className="mono">{optInEmitted.join(', ')}</span>
              </>
            )}
          </div>
        </div>
      </ConfirmDialog>
    </>
  );
}

export default DryRunPanel;
